import * as yup from 'yup'
import { IntlShape, defineMessages } from 'react-intl'
import { isEmpty } from 'lodash'
import { IPreferredSeatsDeckPriceValue } from 'features/Ancillaries/components/PreferredSeatsDeckPriceSettings/types'

const messages = defineMessages({
  required: {
    id: 'dictionary.field.required',
    defaultMessage: 'Field required'
  },
  min: {
    id: 'dictionary.value.min',
    defaultMessage: 'The value must be at least {min} characters long'
  },
  max: {
    id: 'dictionary.value.max',
    defaultMessage: 'The value must be at most {max} characters long'
  },
  validRange: {
    id: 'dictionary.value.validRange',
    defaultMessage: 'Enter valid range'
  },
  rangeAscending: {
    id: 'dictionary.value.rangeAscending',
    defaultMessage: 'The range must be ascending'
  }
})

const deckPricesSchema = (intl: IntlShape): yup.ArraySchema<yup.SchemaOf<IPreferredSeatsDeckPriceValue>> => {
  return yup
    .array()
    .of(
      yup.object().shape({
        fromLabel: yup.string().required(intl.formatMessage(messages.required)),
        toLabel: yup
          .string()
          .required(intl.formatMessage(messages.required))
          .test('is-greater-or-equal', intl.formatMessage(messages.validRange), function (value) {
            const { fromLabel } = this.parent
            return Number(value) >= Number(fromLabel)
          }),
        price: yup.string().required(intl.formatMessage(messages.required))
      })
    )
    .test('is-range-ascending', intl.formatMessage(messages.rangeAscending), function (value) {
      const deckPrices = this.resolve(value) ?? []
      for (let i = 1; i < deckPrices.length; i++) {
        const prevToLabel = Number(deckPrices[i - 1].toLabel)
        const currentFromLabel = Number(deckPrices[i].fromLabel)
        if (prevToLabel >= currentFromLabel) {
          return false
        }
      }
      return true
    })
}
const segmentsValidation = (intl: IntlShape): yup.ArraySchema<yup.SchemaOf<string>> => {
  return yup.array().when('allSegmentsAreSelected', {
    is: false,
    then: yup.array().min(1, intl.formatMessage(messages.required))
  })
}

export const schema = (intl: IntlShape): yup.AnyObjectSchema => {
  return yup.object().shape(
    {
      singleDeckPrices: deckPricesSchema(intl).when('doubleDeckPrices', {
        is: (doubleDeckPrices: IPreferredSeatsDeckPriceValue[]) => {
          return isEmpty(doubleDeckPrices)
        },
        then: yup.array().min(1, intl.formatMessage(messages.required))
      }),
      doubleDeckPrices: deckPricesSchema(intl).when('singleDeckPrices', {
        is: (singleDeckPrices: IPreferredSeatsDeckPriceValue[]) => {
          return isEmpty(singleDeckPrices)
        },
        then: yup.array().min(1, intl.formatMessage(messages.required))
      }),
      salesChannels: yup
        .array()
        .of(
          yup.object().shape({
            id: yup.string().required(),
            name: yup.string().required(),
            country: yup.object()
          })
        )
        .min(1, intl.formatMessage(messages.required)),
      company: yup.object().shape({
        id: yup.string().required(intl.formatMessage(messages.required)),
        name: yup.string()
      }),
      destination: yup.object().shape({
        id: yup.string().required(intl.formatMessage(messages.required)),
        name: yup.string().required(),
        countryCode: yup.string()
      }),
      origin: yup.object().shape({
        id: yup.string().required(intl.formatMessage(messages.required)),
        name: yup.string().required(),
        countryCode: yup.string()
      }),
      countryCode: yup.string().required(intl.formatMessage(messages.required)),
      isReturn: yup.boolean().required(),
      segmentsOrigins: segmentsValidation(intl),
      segmentsDestinations: segmentsValidation(intl),
      segments: segmentsValidation(intl),
      routeServices: yup.array().when('allServicesAreSelected', {
        is: false,
        then: yup.array().min(1, intl.formatMessage(messages.required))
      }),
      allServicesAreSelected: yup.boolean(),
      allSegmentsAreSelected: yup.boolean()
    },
    [['singleDeckPrices', 'doubleDeckPrices']]
  )
}
